import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import quantService from '../../../services/quantService';

const fmt = (v, n = 3) => (v == null || isNaN(v)) ? '—' : Number(v).toFixed(n);
const numColor = (v) =>
  v == null || isNaN(v) ? 'text-gray-400'
  : v > 0 ? 'text-emerald-600 dark:text-emerald-400'
  : v < 0 ? 'text-red-500 dark:text-red-400' : 'text-gray-400';

const BacktestPage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [discoveryId, setDiscoveryId] = useState(params.get('discovery') || '');
  const [discoveries, setDiscoveries] = useState([]);
  const [backtests, setBacktests] = useState([]);
  const [testStart, setTestStart] = useState('2021-01-01');
  const [testEnd, setTestEnd] = useState(new Date().toISOString().slice(0, 10));
  const [rebalanceFreq, setRebalanceFreq] = useState('1W');
  const [portfolioValue, setPortfolioValue] = useState(1_000_000);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const loadAll = async () => {
    try {
      const [d, b] = await Promise.all([
        quantService.listDiscovery(),
        quantService.listBacktest(),
      ]);
      setDiscoveries((d.data || []).filter(j => j.alphalens_passed));
      setBacktests(b.data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  };

  useEffect(() => {
    loadAll();
    const t = setInterval(loadAll, 5000);
    return () => clearInterval(t);
  }, []);

  const submit = async () => {
    setError(null);
    if (!discoveryId) { setError('Pick an Alphalens-passed discovery job.'); return; }
    setSubmitting(true);
    try {
      const { data } = await quantService.runBacktest({
        discovery_job_id: discoveryId,
        test_start: testStart,
        test_end: testEnd,
        rebalance_freq: rebalanceFreq,
        portfolio_value: portfolioValue,
      });
      navigate(`/trading/quant/backtest/${data.job_id}`);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Run form */}
      <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300">
            New Backtest
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Quintile L/S portfolio with Almgren-Chriss costs subtracted manually. Test window is sacred — one look only.
          </p>
        </div>
        <div className="p-4 grid gap-3 md:grid-cols-2">
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1">Discovery (Alphalens-passed)</label>
            <select value={discoveryId} onChange={e => setDiscoveryId(e.target.value)}
                    className="w-full px-3 py-1.5 text-sm bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-gray-800 rounded">
              <option value="">— select —</option>
              {discoveries.map(d => (
                <option key={d.job_id} value={d.job_id}>
                  {d.job_id.slice(0, 8)}… ({d.universe_name}, ICIR {fmt(d.icir, 2)}, {d.n_features_out} features)
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1">Rebalance frequency</label>
            <select value={rebalanceFreq} onChange={e => setRebalanceFreq(e.target.value)}
                    className="w-full px-3 py-1.5 text-sm bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-gray-800 rounded">
              <option value="1D">Daily</option>
              <option value="1W">Weekly</option>
              <option value="2W">Bi-weekly</option>
              <option value="1M">Monthly</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1">Test window</label>
            <div className="flex gap-2">
              <input type="date" value={testStart} onChange={e => setTestStart(e.target.value)}
                     className="flex-1 px-3 py-1.5 text-sm bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-gray-800 rounded" />
              <input type="date" value={testEnd} onChange={e => setTestEnd(e.target.value)}
                     className="flex-1 px-3 py-1.5 text-sm bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-gray-800 rounded" />
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1">Portfolio value ($)</label>
            <input type="number" step="100000" value={portfolioValue} onChange={e => setPortfolioValue(Number(e.target.value))}
                   className="w-full px-3 py-1.5 text-sm bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-gray-800 rounded" />
          </div>
        </div>
        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-800 flex justify-end">
          <button onClick={submit} disabled={submitting}
                  className="px-4 py-1.5 text-sm bg-brand-gold text-white rounded hover:bg-brand-gold-dark disabled:opacity-40">
            {submitting ? 'Queueing…' : '▶ Run Backtest'}
          </button>
        </div>
        {error && <div className="px-4 py-2 text-xs text-red-500 border-t border-gray-200 dark:border-gray-800">{error}</div>}
      </div>

      {/* Recent */}
      <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300">Recent Backtests</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800/50 text-xs uppercase tracking-wider text-gray-500">
              <tr>
                <th className="text-left px-3 py-2">Job</th>
                <th className="text-left px-3 py-2 hidden sm:table-cell">Universe</th>
                <th className="text-right px-3 py-2">Sharpe (net)</th>
                <th className="text-right px-3 py-2 hidden md:table-cell">Alpha</th>
                <th className="text-right px-3 py-2 hidden md:table-cell">Max DD</th>
                <th className="text-right px-3 py-2">DSR</th>
                <th className="text-left px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {backtests.length === 0 && (
                <tr><td colSpan={7} className="text-center py-6 text-gray-400 text-sm">No backtests yet.</td></tr>
              )}
              {backtests.map(b => (
                <tr key={b.job_id}
                    onClick={() => navigate(`/trading/quant/backtest/${b.job_id}`)}
                    className="hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer border-t border-gray-100 dark:border-gray-800/50">
                  <td className="px-3 py-2 font-mono text-xs">{b.job_id.slice(0, 8)}…</td>
                  <td className="px-3 py-2 text-xs hidden sm:table-cell">{b.discovery_universe}</td>
                  <td className={`px-3 py-2 text-right text-xs font-mono ${numColor(b.sharpe_net)}`}>{fmt(b.sharpe_net, 2)}</td>
                  <td className={`px-3 py-2 text-right text-xs font-mono hidden md:table-cell ${numColor(b.alpha_annual)}`}>
                    {b.alpha_annual != null ? `${(b.alpha_annual * 100).toFixed(1)}%` : '—'}
                  </td>
                  <td className={`px-3 py-2 text-right text-xs font-mono hidden md:table-cell ${numColor(b.max_drawdown)}`}>
                    {b.max_drawdown != null ? `${(b.max_drawdown * 100).toFixed(1)}%` : '—'}
                  </td>
                  <td className="px-3 py-2 text-right text-xs">
                    {b.dsr_verdict
                      ? <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider ${
                          b.dsr_verdict === 'REAL' ? 'bg-emerald-100 text-emerald-700' :
                          b.dsr_verdict === 'LIKELY' ? 'bg-blue-100 text-blue-700' :
                          b.dsr_verdict === 'UNCERTAIN' ? 'bg-amber-100 text-amber-700' :
                          'bg-red-100 text-red-700'
                        }`}>{b.dsr_verdict}</span>
                      : '—'}
                  </td>
                  <td className="px-3 py-2 text-xs">{b.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default BacktestPage;
